import { useState, useEffect } from 'react';
import { LogOut, Sparkles, Clock, RefreshCw, FileText, ArrowRight, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useEmailContext } from '../hooks/useEmailContext';
import {
  fetchAgents, sendEmailToAgent, fetchSessions, fetchHistory,
  fetchTemplates, saveTemplate, deleteTemplate,
  fetchTonePreference, saveTonePreference,
} from '../services/api';
import type { Agent, SessionHistoryItem, ContentAsset } from '../services/api';
import { WorkstationPicker } from './WorkstationPicker';
import { EmailPreview } from './EmailPreview';
import { ResponseCard } from './ResponseCard';
import { TabsNavigation } from './TabsNavigation';
import { ChatTab } from './ChatTab';

// Agentes creados para este addin deben tener type="outlook" en la web de administración.
const AGENT_TYPE = 'outlook';

const generateSessionId = () => {
  return 'outlook-' + Math.random().toString(36).substring(2, 15) + '-' + Math.random().toString(36).substring(2, 15);
};

export function TaskPane() {
  const { account, token, user, loading: authLoading, error: authError, login, logout } = useAuth();
  const { email, loading: emailLoading } = useEmailContext();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [chatLoadRequest, setChatLoadRequest] = useState<{ sessionId: string; agentId: string; nonce: number } | null>(null);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [response, setResponse] = useState('');
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');

  // Tabs and History / Templates states
  const [activeTab, setActiveTab] = useState('chat');
  const [sessionId, setSessionId] = useState(generateSessionId);
  const [sessions, setSessions] = useState<SessionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyAgentFilter, setHistoryAgentFilter] = useState('');
  const [templates, setTemplates] = useState<ContentAsset[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [tone, setTone] = useState('');
  const [toneSaved, setToneSaved] = useState(false);

  useEffect(() => {
    if (!token || !user) return;
    // Solo agentes clasificados para este addin (type="outlook"); si todavía no
    // hay ninguno tageado así, cae de vuelta a mostrar todos para no dejar el
    // addin vacío durante la transición.
    fetchAgents(token, user, AGENT_TYPE)
      .then(list => (list.length > 0 ? list : fetchAgents(token, user)))
      .then(setAgents)
      .catch(() => setError('No se pudieron cargar los agentes'));
  }, [token, user]);

  // Chat libre: cualquier agente disponible, sin el filtro type="outlook" de arriba.
  useEffect(() => {
    if (!token || !user) return;
    fetchAgents(token, user).then(setAllAgents).catch(() => {});
  }, [token, user]);

  // Load history/sessions — opcionalmente scopeado a un solo agente
  useEffect(() => {
    if (activeTab === 'history' && token && user) {
      setHistoryLoading(true);
      setError('');
      fetchSessions(token, user, 'chat', historyAgentFilter || undefined)
        .then(setSessions)
        .catch(() => setError('No se pudo cargar el historial'))
        .finally(() => setHistoryLoading(false));
    }
  }, [activeTab, token, user, historyAgentFilter]);

  // Load templates + tone preference — scoped to the currently selected agent, ya que
  // memoria/tono/plantillas nunca se mezclan entre agentes distintos.
  useEffect(() => {
    if (activeTab === 'templates' && token && user && selectedAgent) {
      setTemplatesLoading(true);
      setError('');
      Promise.all([
        fetchTemplates(token, user, selectedAgent),
        fetchTonePreference(token, user, selectedAgent),
      ])
        .then(([tpls, tonePref]) => {
          setTemplates(tpls);
          setTone(tonePref);
        })
        .catch(() => setError('No se pudieron cargar las plantillas'))
        .finally(() => setTemplatesLoading(false));
    }
  }, [activeTab, token, user, selectedAgent]);

  const processEmail = async () => {
    if (!token || !email || !selectedAgent) return;
    const ws = agents.find(a => a.id === selectedAgent);
    if (!ws) return;

    setProcessing(true);
    setError('');
    setResponse('');
    setStatusMessage('');

    try {
      const result = await sendEmailToAgent(
        token,
        user!,
        ws.id,
        email.body,
        email.subject,
        email.sender,
        customInstructions,
        sessionId,
        // Cada evento "thinking" trae solo el fragmento nuevo, no el acumulado — reemplazar
        // directo hacía que el texto del botón saltara/perdiera contexto a mitad de una idea.
        chunk => setStatusMessage(prev => prev + chunk),
      );
      setResponse(result.response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error inesperado');
    } finally {
      setProcessing(false);
      setStatusMessage('');
    }
  };

  const startNewInteraction = () => {
    setSessionId(generateSessionId());
    setResponse('');
    setCustomInstructions('');
    setError('');
  };

  const selectHistorySession = async (sessId: string, agentId: string) => {
    // Historial/sesiones son compartidas entre el flujo de "Responder correo" y el
    // de "Chat" (mismo endpoint /chat/{agentId}, mismo session_id) — hay que
    // enrutar cada sesión a la pestaña donde puede reconstruirse correctamente:
    // el agente de esta sesión no está entre los type="outlook" -> vino del chat libre.
    const isOutlookAgent = agents.some(a => a.id === agentId);
    if (!isOutlookAgent) {
      setActiveTab('freechat');
      setChatLoadRequest({ sessionId: sessId, agentId, nonce: Date.now() });
      return;
    }

    setSessionId(sessId);
    setSelectedAgent(agentId);
    setProcessing(true);
    setActiveTab('chat');
    setError('');
    try {
      const hist = await fetchHistory(token!, user!, sessId);
      const agentMsgs = hist.filter(m => m.sender === 'agent' || m.sender === 'assistant');
      if (agentMsgs.length > 0) {
        setResponse(agentMsgs[agentMsgs.length - 1].text);
      } else {
        setResponse('');
      }
    } catch (err) {
      setError('No se pudo cargar la conversación seleccionada');
    } finally {
      setProcessing(false);
    }
  };

  const useTemplate = (asset: ContentAsset) => {
    // Se embebe el contenido exacto de la plantilla en las instrucciones —
    // reproducción verbatim garantizada, sin depender de que el modelo la
    // recupere correctamente durante el loop de tools.
    setCustomInstructions(
      `Usa exactamente esta plantilla como base de la respuesta (no la parafrasees):\n\n${asset.content}`
    );
    setActiveTab('chat');
  };

  const saveResponseAsTemplate = async (content: string) => {
    if (!token || !user || !selectedAgent) return;
    const name = window.prompt('Nombre para esta plantilla:');
    if (!name || !name.trim()) return;
    try {
      await saveTemplate(token, user, selectedAgent, name.trim(), content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la plantilla');
    }
  };

  const removeTemplate = async (asset: ContentAsset) => {
    if (!token || !user) return;
    try {
      await deleteTemplate(token, user, asset.id);
      setTemplates(prev => prev.filter(t => t.id !== asset.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar la plantilla');
    }
  };

  const saveTone = async () => {
    if (!token || !user || !selectedAgent) return;
    try {
      await saveTonePreference(token, user, selectedAgent, tone);
      setToneSaved(true);
      setTimeout(() => setToneSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la preferencia de tono');
    }
  };

  // ── Auth screen ────────────────────────────────────────────────────────────
  if (authLoading || emailLoading) {
    return (
      <div className="center">
        <div className="spinner" />
        <p className="muted">Iniciando…</p>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="center">
        <img
          src="https://cdaraucania.sfo2.cdn.digitaloceanspaces.com/01.resources/02.cdaraucania/01.img/da-logo.png"
          alt="Desarrolla Araucanía"
          className="logo"
        />
        <p className="muted">Inicia sesión con tu cuenta corporativa para usar el asistente IA.</p>
        {authError && <p className="error-text">{authError}</p>}
        <button className="btn-primary" onClick={login}>
          Iniciar sesión
        </button>
      </div>
    );
  }

  // ── Main taskpane ──────────────────────────────────────────────────────────
  return (
    <div className="taskpane">
      <header className="tp-header">
        <div className="tp-header-left">
          <img
            src="https://cdaraucania.sfo2.cdn.digitaloceanspaces.com/01.resources/02.cdaraucania/01.img/da-logo.png"
            alt="DA"
            className="logo-sm"
          />
          <span className="tp-title">Asistente IA</span>
        </div>
        <div className="tp-header-right">
          <span className="user-name">{account.name?.split(' ')[0]}</span>
          <button className="btn-ghost" onClick={logout} title="Cerrar sesión">
            <LogOut size={15} />
          </button>
        </div>
      </header>

      <TabsNavigation activeTab={activeTab} onChange={setActiveTab} />

      <div className="tp-body">
        {error && <p className="error-text">{error}</p>}

        {activeTab === 'chat' && (
          <>
            <div className="btn-new-chat-container">
              <button className="btn-ghost btn-outline" onClick={startNewInteraction} disabled={processing} title="Nueva interacción">
                <Plus size={13} /> Nueva interacción
              </button>
            </div>

            {email ? (
              <EmailPreview email={email} />
            ) : (
              <p className="muted">Abre un correo para procesarlo.</p>
            )}

            <WorkstationPicker
              agents={agents}
              selected={selectedAgent}
              onChange={setSelectedAgent}
              disabled={processing}
            />

            <div className="field-group">
              <label className="field-label">
                Instrucciones adicionales <span className="optional">(opcional)</span>
              </label>
              <textarea
                className="textarea"
                rows={3}
                placeholder="Ej: Responde en tono formal, incluye referencia al contrato N° 2024-001…"
                value={customInstructions}
                onChange={e => setCustomInstructions(e.target.value)}
                disabled={processing}
              />
            </div>

            <button
              className="btn-primary btn-full"
              onClick={processEmail}
              disabled={processing || !email || !selectedAgent}
            >
              {processing ? (
                <><span className="spinner-sm" /> {statusMessage || 'Procesando…'}</>
              ) : (
                <><Sparkles size={14} /> Generar respuesta</>
              )}
            </button>

            {response && (
              <ResponseCard
                response={response}
                onRegenerate={processEmail}
                onSaveAsTemplate={saveResponseAsTemplate}
                loading={processing}
              />
            )}
          </>
        )}

        {activeTab === 'freechat' && (
          <ChatTab token={token!} user={user!} agents={allAgents} loadRequest={chatLoadRequest} email={email} />
        )}

        {activeTab === 'history' && (
          <div className="history-list">
            <h3 className="field-label">Historial de Interacciones</h3>
            <select
              className="select"
              value={historyAgentFilter}
              onChange={e => setHistoryAgentFilter(e.target.value)}
              style={{ marginBottom: '4px' }}
            >
              <option value="">Todos los agentes</option>
              {allAgents.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {historyLoading ? (
              <div className="center" style={{ minHeight: '150px' }}>
                <div className="spinner" />
              </div>
            ) : sessions.length === 0 ? (
              <p className="muted">No hay interacciones previas.</p>
            ) : (
              sessions.map(s => (
                <div key={s.id} className="history-item" onClick={() => selectHistorySession(s.id, s.agent_id)}>
                  <div className="history-item-header">
                    <span className="history-item-title">{s.title || 'Conversación'}</span>
                    <span className="history-item-date">
                      {new Date(s.timestamp).toLocaleDateString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="history-item-agent">
                    <MessageSquare size={11} />
                    <span>{s.agent_name}</span>
                  </div>
                  <p className="history-item-snippet">{s.last_message}</p>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'templates' && !selectedAgent && (
          <div className="templates-list">
            <p className="muted">Selecciona un agente en la pestaña de Chat para ver y guardar su tono y plantillas.</p>
          </div>
        )}

        {activeTab === 'templates' && selectedAgent && (
          <div className="templates-list">
            <h3 className="field-label">Tono preferido</h3>
            <textarea
              className="textarea"
              rows={2}
              placeholder="Ej: Cercano y directo, evita formalismos excesivos…"
              value={tone}
              onChange={e => setTone(e.target.value)}
            />
            <button className="btn-ghost btn-outline" onClick={saveTone} style={{ marginTop: '6px' }}>
              {toneSaved ? 'Guardado ✓' : 'Guardar tono'}
            </button>

            <h3 className="field-label" style={{ marginTop: '16px' }}>Plantillas Disponibles</h3>
            {templatesLoading ? (
              <div className="center" style={{ minHeight: '150px' }}>
                <div className="spinner" />
              </div>
            ) : templates.length === 0 ? (
              <p className="muted">No hay plantillas guardadas todavía. Genera una respuesta y usa "Guardar como plantilla".</p>
            ) : (
              templates.map(t => (
                <div key={t.id} className="template-item" style={{ marginTop: '4px' }}>
                  <div className="template-info">
                    <FileText size={14} color="var(--primary-orange)" />
                    <span className="template-name" title={t.content}>
                      {t.name}{t.owner_id === null ? ' (institucional)' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button className="btn-ghost" onClick={() => useTemplate(t)} title="Usar esta plantilla">
                      <ArrowRight size={14} />
                    </button>
                    {t.owner_id !== null && (
                      <button className="btn-ghost" onClick={() => removeTemplate(t)} title="Eliminar plantilla">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
